import * as cache from './offer-cache';
import { toYmd } from '../availability-calendar/pricing';
import { formatCurrency } from '../../lib/helpers';
import type { OfferPrice } from '../../lib/types';
import type { PriceSummaryElements } from './price-calculator';
import { updatePriceSummary } from './price-calculator';

export type PricingMode = 'per-night' | 'per-day' | 'flat-seats';

export function parsePricingMode(raw: string): PricingMode {
    if (raw === 'per-night' || raw === 'per-day' || raw === 'flat-seats') return raw;
    throw new Error(`Unknown pricing mode: "${raw}"`);
}

function dateOnly(iso: string): string {
    return iso.substring(0, 10);
}

function computeLineTotal(
    prices: OfferPrice[],
    fromStr: string,
    toStr: string,
    inclusive: boolean
): number {
    if (!fromStr || !toStr) return 0;
    // Use Date.UTC to avoid DST hour-length ambiguity.
    const [fy, fm, fd] = fromStr.split('-').map(Number);
    const [ty, tm, td] = toStr.split('-').map(Number);
    const totalDays = Math.round(
        (Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000,
    );
    const iterations = inclusive ? totalDays + 1 : totalDays;
    if (iterations <= 0) return 0;

    let total = 0;
    // Build a Date from local components so toYmd() stays in local timezone.
    const cursor = new Date(fy, fm - 1, fd);
    for (let i = 0; i < iterations; i++) {
        const curStr = toYmd(cursor);
        let covering: OfferPrice | undefined;
        for (const price of prices) {
            const pFrom = dateOnly(price.from || '');
            const pTo   = dateOnly(price.to   || '');
            // inclusive To: pFrom <= curStr <= pTo (last-wins by id on overlap)
            if (pFrom <= curStr && curStr <= pTo) {
                if (!covering || price.id > covering.id) covering = price;
            }
        }
        if (covering) total += covering.price;
        cursor.setDate(cursor.getDate() + 1);
    }
    return Math.round(total * 100) / 100;
}

// Build unitId → prices map once per cache snapshot to avoid O(N×M) per event.
let _unitPriceMapGeneration = 0;
let _unitPriceMap = new Map<number, OfferPrice[]>();

function getUnitPriceMap(): Map<number, OfferPrice[]> {
    const all = cache.getAll();
    const gen = Object.keys(all).length;
    if (gen !== _unitPriceMapGeneration) {
        _unitPriceMap = new Map();
        _unitPriceMapGeneration = gen;
        for (const offer of Object.values(all)) {
            for (const unit of offer.offerUnits || []) {
                _unitPriceMap.set(unit.id, unit.offerPrices || []);
            }
        }
    }
    return _unitPriceMap;
}

function findUnitPrices(unitId: number): OfferPrice[] {
    return getUnitPriceMap().get(unitId) ?? [];
}

function refreshCard(card: HTMLElement, pricingMode: PricingMode): void {
    const unitId    = parseInt(card.dataset.unitId || '0', 10);
    const fromInput = card.querySelector<HTMLInputElement>('.sub-range-from-picker');
    const toInput   = card.querySelector<HTMLInputElement>('.sub-range-to-picker');
    const totalEl   = card.querySelector<HTMLElement>('[data-line-total]');
    if (!fromInput || !toInput || !totalEl) return;

    const fromStr   = fromInput.value;
    const toStr     = toInput.value;
    const inclusive = pricingMode === 'per-day';
    const prices    = findUnitPrices(unitId);
    const total     = computeLineTotal(prices, fromStr, toStr, inclusive);

    totalEl.setAttribute('data-line-total', String(total));
    totalEl.textContent = (fromStr && toStr) ? formatCurrency(total) : '–';
}

// Track already-wired containers so repeated calls on the same element are no-ops.
const _wiredContainers = new WeakSet<HTMLElement>();

export function setupSubRangePricing(
    container: HTMLElement,
    pricingModeRaw: string,
    summaryElements: PriceSummaryElements,
    onPriceChange: () => void
): void {
    if (_wiredContainers.has(container)) return;
    _wiredContainers.add(container);

    const pricingMode = parsePricingMode(pricingModeRaw);
    container.addEventListener('change', (e) => {
        const target = e.target as HTMLElement;
        if (!target.classList.contains('sub-range-from-picker') &&
            !target.classList.contains('sub-range-to-picker')) return;

        const card = target.closest<HTMLElement>('.cc-unit-selection-card');
        if (card) refreshCard(card, pricingMode);
        updatePriceSummary(summaryElements);
        onPriceChange();
    });
}

export function invalidateUnitPriceMap(): void {
    _unitPriceMapGeneration = -1;
}
